import { WebsocketClientService } from './websocket-client.service';
import { LocalAvalaibleEnum } from '../_enums/localAvalaible.enum';
import { TelemedicineService } from './telemedicine.service';
import { WebSocketChannelUser } from '../_model/telemedicine/webSocketChannelUser.model';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import AgoraRTC, {
    IAgoraRTCClient,
    ClientConfig,
    IAgoraRTCRemoteUser,
    NetworkQuality,
    ILocalTrack,
    IRemoteAudioTrack,
    IRemoteVideoTrack,
} from 'agora-rtc-sdk-ng';
import { StorageService } from './storage.service';
import { WsCallStateType } from '../_enums/ws-call-state-type.enum';

@Injectable({
    providedIn: 'root',
})
export class TelemedicineAgoraService {
    private client: IAgoraRTCClient;
    private telemedInitialised = false;
    private connected = false;
    private localStream: ILocalTrack;
    private remoteUser: IAgoraRTCRemoteUser;
    private outsideCallInProgress = false;
    private setVolumeTo = 100;
    public audioVolume = 100;
    public audioVolume$: BehaviorSubject<number> = new BehaviorSubject(100);

    private channelId: string = null;

    public localAvalaible: { initial: LocalAvalaibleEnum | null; final: LocalAvalaibleEnum } = {
        initial: null,
        final: LocalAvalaibleEnum.WAITING,
    };

    public localAvalaible$: BehaviorSubject<{
        initial: LocalAvalaibleEnum | null;
        final: LocalAvalaibleEnum;
    }> = new BehaviorSubject(this.localAvalaible);

    private videoDevices$: BehaviorSubject<MediaDeviceInfo[]> = new BehaviorSubject([]);
    private audioDevices$: BehaviorSubject<MediaDeviceInfo[]> = new BehaviorSubject([]);
    private playoutDevices$: BehaviorSubject<MediaDeviceInfo[]> = new BehaviorSubject([]);

    private activeDevices: {
        camera: MediaStreamTrack;
        audio: MediaStreamTrack;
        playout: MediaDeviceInfo;
    } = {
        camera: null,
        audio: null,
        playout: null,
    };

    private activeDevices$: BehaviorSubject<{
        camera: MediaStreamTrack;
        audio: MediaStreamTrack;
        playout: MediaDeviceInfo;
    }> = new BehaviorSubject(this.activeDevices);

    private localAudioEnabled = false;
    private localVideoEnabled = false;

    private lastFallBackCombination = 0;
    private fallBackCombinations = [
        {
            video: true,
            audio: true,
        },
        {
            video: true,
            audio: false,
        },
        {
            video: false,
            audio: true,
        },
    ];

    private streamProfiles = {
        '720p': {
            agoraProfile: '720p_3',
            audio: true,
            video: true,
        },
        '480p': {
            agoraProfile: '480p_1',
            audio: true,
            video: true,
        },
    };
    private localAudio;
    private localVideo;

    constructor(
        private storage: StorageService,
        private wsClient: WebsocketClientService,
        private telemedRest: TelemedicineService,
    ) {}

    public async initAgora(channelId: string): Promise<Observable<boolean>> {
        const response: ReplaySubject<boolean> = new ReplaySubject(1);

        if (this.telemedInitialised) {
            response.next(true);
            response.complete();
            return response;
        }

        AgoraRTC.setLogLevel(environment.agora.logLevel);

        if (channelId) {
            this.channelId = channelId;
        }
        if (!this.client) {
            const agoraConf: ClientConfig = {
                mode: 'rtc',
                codec: 'vp8',
            };

            this.client = AgoraRTC.createClient(agoraConf);
            this.assignClientHandlers();
        } else {
            response.next(true);
            response.complete();
        }

        this.telemedInitialised = true;

        return response;
    }

    public initLocalMedia(uid: number, profileId?: string, channelId?: string): Promise<void> {
        return new Promise<void>((resolve) => {
            this.initAgora(channelId).then(async () => {
                const combination = this.fallBackCombinations[this.lastFallBackCombination];
                this.localAudio = await AgoraRTC.createMicrophoneAudioTrack();

                try {
                    this.localVideo = await AgoraRTC.createCameraVideoTrack({
                        encoderConfig: this.getProfile(profileId).agoraProfile,
                    });

                    this.setLocalAvalaible(LocalAvalaibleEnum.READY);
                    console.log('getUserMedia successfully');
                    this.connected = false;

                    this.toggleAudio(combination.audio);
                    this.toggleVideo(combination.video);

                    console.log(this.localStream);
                    this.localVideo.play('agora-local-video');

                    this.wsClient.sendSocketMessage(channelId, {
                        cameraReady: combination.video,
                        microphoneReady: combination.audio,
                        cameraError: !combination.video,
                        microphoneError: !combination.audio,
                    });

                    resolve();
                } catch (e) {
                    console.error('getUserMedia failed', e);
                    this.setLocalAvalaible(LocalAvalaibleEnum[e.msg]);

                    this.lastFallBackCombination++;
                    if (this.lastFallBackCombination < this.fallBackCombinations.length) {
                        this.localStream.close();
                        this.initLocalMedia(uid);
                        return;
                    }

                    this.toggleAudio(false);
                    this.toggleVideo(false);

                    this.wsClient.sendSocketMessage(this.channelId, {
                        cameraReady: false,
                        cameraError: true,
                        microphoneReady: false,
                        microphoneError: true,
                    });
                }

                this.assignLocalStreamHandlers();
            });
        });
    }

    private assignLocalStreamHandlers(): void {
        this.loadCameraDevices();
        this.loadRecordingDevices();
        this.loadPlayoutDevices();
    }

    private assignClientHandlers(): void {
        this.client.on('connection-state-change', (evt) => {
            if (evt === 'CONNECTED') {
                this.wsClient.sendSocketMessage(this.channelId, {
                    streamPublished: true,
                });
                console.log('Publish local stream successfully');
            }
        });

        this.client.on('exception', (error) => {
            console.error('Got error msg:', error.msg);
            if (error.msg === 'DYNAMIC_KEY_TIMEOUT') {
                try {
                    this.client.renewToken('');
                    console.log('Renewed the channel key successfully.');
                } catch (e) {
                    console.error('Renew channel key failed: ', e);
                }
            }
        });

        this.client.on('user-published', async (user, mediaType) => {
            await this.client.subscribe(user, mediaType);
            if (mediaType === 'video') {
                console.log('subscribe video success');
                user.videoTrack.play('agora-remote-video');
            }
            if (mediaType === 'audio') {
                console.log('subscribe audio success');
                user.audioTrack.play();
            }
        });

        this.client.enableAudioVolumeIndicator();

        this.client.on('volume-indicator', function (result) {
            result.forEach(function (volume, index) {
                console.log(`${index} UID ${volume.uid} Level ${volume.level}`);
            });
        });

        this.client.on('network-quality', (stats: NetworkQuality) => {
            console.log('downlinkNetworkQuality', stats.downlinkNetworkQuality);
            console.log('uplinkNetworkQuality', stats.uplinkNetworkQuality);
        });

        this.client.on('user-joined', (evt) => {
            const stream = evt.videoTrack as IRemoteVideoTrack;
            this.wsClient.setCallState(WsCallStateType.CALL_STARTED);
            this.setAudioVolume(this.setVolumeTo).then();

            this.wsClient.sendSocketMessage(this.channelId, {
                inCall: true,
            });
            this.wsClient.sendSocketStartMessage(this.channelId);

            setTimeout(() => {
                console.log('stream.play(agora-remote-video)');
                stream.play('agora-remote-video');
            }, 50);

            this.wsClient.callTimer(true);
        });

        this.client.on('user-left', (evt: IAgoraRTCRemoteUser) => {
            const oldStream = evt.videoTrack as IRemoteVideoTrack;

            if (oldStream) {
                oldStream.stop();
                console.log(`${evt.uid} left from this channel`);
            }
        });

        this.client.on('user-published', (evt) => {
            this.remoteUser = evt;
            console.log(`${evt.uid} online on agora`);
        });
    }

    join(remoteClient: WebSocketChannelUser, channelId?: string, contractorId?: string, accessToken?: string): void {
        if (this.connected) {
            return;
        }

        this.telemedRest
            .getTokenData(
                this.wsClient.getLocalUserType(this.channelId) === 'doctor',
                this.channelId,
                this.storage.getVendor(),
                this.storage.getContractorId(),
            )
            .subscribe(async (response) => {
                try {
                    if (!environment.agora.appId) {
                        throw new Error('Cannot join Agora — appId is missing!');
                    }

                    await this.client.join(environment.agora.appId, this.channelId, response.token, response.uid);

                    this.wsClient.sendSocketMessage(this.channelId, {
                        clientJoined: true,
                        uid: response.uid,
                    });
                    this.connected = true;
                    setTimeout(() => {
                        this.publish();
                    }, 10);

                    // @TODO Should be turned around
                    if (remoteClient.cameraError && remoteClient.microphoneError) {
                        if (!this.outsideCallInProgress) {
                            this.setAudioVolume(this.setVolumeTo);
                            this.wsClient.setCallState(WsCallStateType.CALL_STARTED);
                            this.wsClient.sendSocketMessage(this.channelId, {
                                inCall: true,
                            });
                            this.wsClient.sendSocketStartMessage(this.channelId);
                        } else {
                            this.wsClient.sendSocketMessage(this.channelId, {
                                streamPublished: false,
                            });
                            this.connected = false;
                            this.client.leave();
                        }
                    }
                } catch (e) {
                    this.wsClient.sendSocketMessage(this.channelId, {
                        clientJoined: false,
                        inCall: false,
                        uid: response.uid,
                    });
                }
            });
    }

    leave(): void {
        console.log('leave', this.connected);
        if (this.connected) {
            this.unpublish();
            this.unpublish();
            // @TODO: Ignored with eslint-interactive on 2023-10-27
            // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function
            this.client.leave().then((r) => {});

            this.localAudio.close();
            this.localVideo.close();
            this.client.on('connection-state-change', (curState, prevState) => {
                console.log('current', curState, 'prev', prevState);
            });

            this.client.on('user-left', (evt, reason) => {
                console.log(evt);
                console.log(reason);

                this.connected = false;
                this.wsClient
                    .getCallState()
                    .pipe(take(1))
                    .subscribe((callState) => {
                        if (callState === WsCallStateType.CALL_STARTING) {
                            this.wsClient.setCallState(WsCallStateType.DEVICE_TEST);
                        }

                        if (callState === WsCallStateType.CALL_STARTED) {
                            this.wsClient.setCallState(WsCallStateType.CALL_ENDING);
                        }

                        this.wsClient.sendSocketMessage(this.channelId, {
                            clientJoined: false,
                            inCall: false,
                        });
                    });
            });
            this.wsClient.callTimer(false);
        } else {
            this.connected = false;
            this.localAudio?.close();
            this.localVideo?.close();
        }
    }

    async publish() {
        try {
            await this.client.publish([this.localAudio, this.localVideo]);
            console.log('publish success');

            this.wsClient.sendSocketMessage(this.channelId, {
                streamPublished: true,
            });
        } catch (e) {
            console.error('Publish local stream error: ' + e);
        }
    }

    private unpublish(): void {
        this.client.unpublish([this.localAudio, this.localVideo]).catch((error) => {
            console.error(error);
        });
        this.wsClient.sendSocketMessage(this.channelId, {
            streamPublished: false,
        });
    }

    public getLocalAvalaible(): Observable<{ initial: LocalAvalaibleEnum | null; final: LocalAvalaibleEnum }> {
        return this.localAvalaible$;
    }

    private setLocalAvalaible(status: LocalAvalaibleEnum) {
        if (this.localAvalaible.initial === null) {
            this.localAvalaible.initial = status;
        }
        this.localAvalaible.final = status;
        this.localAvalaible$.next(this.localAvalaible);
    }

    private loadCameraDevices() {
        AgoraRTC.getCameras().then((devices: MediaDeviceInfo[]) => {
            const filtered = devices.filter((d) => d.deviceId !== 'default');
            this.videoDevices$.next(filtered);
        });
    }

    public getCameras(): Observable<MediaDeviceInfo[]> {
        return this.videoDevices$.asObservable();
    }

    private loadRecordingDevices() {
        AgoraRTC.getMicrophones().then((devices: MediaDeviceInfo[]) => {
            const filtered = devices.filter((d) => d.deviceId !== 'default');
            this.audioDevices$.next(filtered);
        });
    }

    public getRecordingDevices(): Observable<MediaDeviceInfo[]> {
        return this.audioDevices$.asObservable();
    }

    private loadPlayoutDevices() {
        AgoraRTC.getPlaybackDevices().then((devices: MediaDeviceInfo[]) => {
            const filtered = devices.filter((d) => d.deviceId !== 'default');
            this.playoutDevices$.next(filtered);

            if (!this.activeDevices.playout && filtered.length > 0) {
                this.setActiveDevices(null, null, filtered[0]);
            }
        });
    }

    public getPlayoutDevices(): Observable<MediaDeviceInfo[]> {
        return this.playoutDevices$.asObservable();
    }

    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async switchCamera(camera: any) {
        await this.localVideo.setDevice(camera.deviceId);
        this.setActiveDevices(camera.label);
    }

    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async switchAudio(audio: any) {
        await this.localAudio.setDevice(audio.deviceId);
        this.setActiveDevices(null, audio.label);
    }

    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async switchPlayoutDevice(device: any) {
        await this.remoteUser?.audioTrack?.setPlaybackDevice(device.deviceId);
        this.setActiveDevices(null, null, device);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    private setActiveDevices(camera?, audio?, playout?) {
        if (camera) {
            this.activeDevices.camera = camera;
        }

        if (audio) {
            this.activeDevices.audio = audio;
        }

        if (playout) {
            this.activeDevices.playout = playout;
        }

        this.activeDevices$.next(this.activeDevices);
    }

    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getActiveDevices(): Observable<any> {
        return this.activeDevices$;
    }

    async setAudioVolume(setTo: number = null) {
        if (setTo !== null) {
            this.setVolumeTo = setTo;
        } else {
            if (this.audioVolume > 50) {
                this.setVolumeTo = 50;
            } else if (this.audioVolume > 0) {
                this.setVolumeTo = 0;
            } else if (this.audioVolume === 0) {
                this.setVolumeTo = 100;
            }
        }
        this.audioVolume = this.setVolumeTo;
        if (this.remoteUser) {
            this.remoteUser?.audioTrack?.setVolume(this.setVolumeTo);
        }
        this.wsClient.sendSocketMessage(this.channelId, {
            playoutMuted: this.setVolumeTo <= 0,
        });

        this.audioVolume$.next(setTo);
    }

    async toggleAudio(forceTo?: boolean) {
        if (!this.localAudioEnabled && this.localAudioEnabled !== forceTo) {
            await this.localAudio.setMuted(false);
            this.localAudioEnabled = true;
        } else if (this.localAudioEnabled !== forceTo) {
            await this.localAudio.setMuted(true);
            this.localAudioEnabled = false;
        }
        this.wsClient.sendSocketMessage(this.channelId, {
            microphoneMuted: !this.localAudioEnabled,
        });
    }

    async toggleVideo(forceTo?: boolean) {
        if (!this.localVideoEnabled && this.localVideoEnabled !== forceTo) {
            await this.localVideo.setMuted(false);
            this.localVideoEnabled = true;
        } else if (this.localVideoEnabled !== forceTo) {
            await this.localVideo.setMuted(true);
            this.localVideoEnabled = false;
        }
        console.log('cameraMuted', !this.localVideoEnabled);
        this.wsClient.sendSocketMessage(this.channelId, {
            cameraMuted: !this.localVideoEnabled,
        });
    }

    private getProfile(profileId: string) {
        let profile = '720p';

        if (this.streamProfiles[profileId]) {
            profile = profileId;
        }
        return this.streamProfiles[profile];
    }

    public checkSupportedSystem(): boolean {
        return AgoraRTC.checkSystemRequirements();
    }
}
